import { createSelector } from '@reduxjs/toolkit';
import {
    Order,
    OrderStatus,
    OrderStatusReqLineItemStatusEnum,
    OSKScene,
    SceneFromCapture,
    SceneFromStacItem,
    SigmaAPI,
    StacAPI,
} from 'oskcore';
import { StacItem } from 'stac-ts';
import { SHORT_VISIBLE_PAGE_SIZE } from '~/pagination';
import { AppDispatch, RootState } from '~/redux/store';

export type OrderStatusMapEntry = {
    name: string;
    bg: string;
    fg: string;
    icon: string;
};

// TODO: Add support for the new tasking order codes, or create a separate status map
export const OrderStatusMap: Record<string, OrderStatusMapEntry> = {
    manual: { name: 'Manual Verification', bg: '#FBC414', icon: 'ellipsis', fg: '#282828' },
    mod_received: { name: 'Mod Received', bg: '#EFEEEB', icon: 'ellipsis', fg: '#282828' },
    mod_accepted: { name: 'Mod Accepted', bg: '#EFEEEB', icon: 'ellipsis', fg: '#282828' },
    mod_rejected: { name: 'Mod Rejected', bg: '#FFC6C4', icon: 'times-circle', fg: '#282828' },
    order_received: { name: 'Order Received', bg: '#EFEEEB', icon: 'ellipsis', fg: '#282828' },
    order_accepted: { name: 'Order Accepted', bg: '#EFEEEB', icon: 'ellipsis', fg: '#282828' },
    order_rejected: { name: 'Order Rejected', bg: '#FFC6C4', icon: 'times-circle', fg: '#282828' },
    order_active: { name: 'Order Active', bg: '#A0D1FF', icon: 'ellipsis', fg: '#282828' },
    order_expired: { name: 'Order Expired', bg: '#FFC6C4', icon: 'times-circle', fg: '#282828' },
    order_scheduled: { name: 'Order Scheduled', bg: '#A0D1FF', icon: 'ellipsis', fg: '#282828' },
    order_tasked: { name: 'Order Tasked', bg: '#A0D1FF', icon: 'ellipsis', fg: '#282828' },
    order_processing: { name: 'Order Processing', bg: '#A0D1FF', icon: 'ellipsis', fg: '#282828' },
    order_delivered: { name: 'Order Delivered', bg: '#ABFBB3', icon: 'check', fg: '#282828' },
    order_canceled: { name: 'Order Canceled', bg: '#FFC6C4', icon: 'times-circle', fg: '#282828' },
    order_failed: { name: 'Order Failed', bg: '#FFC6C4', icon: 'times-circle', fg: '#282828' },
};

export const TaskingOrderStatusMap: Record<string, OrderStatusMapEntry> = {
    received: { name: 'Received', bg: '#EFEEEB', icon: 'ellipsis', fg: '#282828' },
    accepted: { name: 'Accepted', bg: '#EFEEEB', icon: 'ellipsis', fg: '#282828' },
    active: { name: 'Active', bg: '#EFEEEB', icon: 'ellipsis', fg: '#282828' },
    scheduled: { name: 'Scheduled', bg: '#EFEEEB', icon: 'ellipsis', fg: '#282828' },
    tasked: { name: 'Tasked', bg: '#EFEEEB', icon: 'ellipsis', fg: '#282828' },
    processing: { name: 'Processing', bg: '#EFEEEB', icon: 'ellipsis', fg: '#282828' },
    processed: { name: 'Processed', bg: '#EFEEEB', icon: 'ellipsis', fg: '#282828' },
    delivered: { name: 'Delivered', bg: '#EFEEEB', icon: 'ellipsis', fg: '#282828' },
    rejected: { name: 'Rejected', bg: '#EFEEEB', icon: 'ellipsis', fg: '#282828' },
    cancelled: { name: 'Cancelled', bg: '#EFEEEB', icon: 'ellipsis', fg: '#282828' },
    expired: { name: 'Expired', bg: '#EFEEEB', icon: 'ellipsis', fg: '#282828' },
    failed: { name: 'Failed', bg: '#EFEEEB', icon: 'ellipsis', fg: '#282828' },

    completed: { name: 'Delivered', bg: '#EFEEEB', icon: 'ellipsis', fg: '#282828' },
};

/** The default style fallback for statuses that aren't found in the regular map */
export const DEFAULT_ORDER_STATUS_STYLE: OrderStatusMapEntry = {
    name: '',
    bg: '#EFEEEB',
    icon: 'ellipses',
    fg: '#282828',
};

export type OrderStatusMessageMapEntry = {
    /** If present, the color to use when displaying reqLineStatusCode and reqLineStatusText.
     * If absent, an indication that these values should not be displayed. */
    color?: string;
    error?: boolean;
};

export const OrderStatusMessageMap: Record<string, OrderStatusMessageMapEntry> = {
    AA: { error: true, color: '#f00' }, // CIOrder fails Feasibility
    AB: { error: true, color: '#f00' }, // CIOrder Fails Business Rules
    AC: { error: true, color: '#f00' }, // Invalid AOI requested on CIOrder
    AD: { error: true, color: '#f00' }, // Invalid inventoryID requested on CIOrder
    AE: { error: true, color: '#f00' }, // Invalid Delivery Information requested on CIOrder
    AH0: { error: false, color: '#000' }, // Line item on CDP's constellation schedule
    AH1: { error: false, color: '#000' }, // Line item uplinked to CDP's constellation; Level 1 Satisfaction
    PS: { error: false, color: '#000' }, // Production Started
    AI: {}, // All increments for the line item have delivered.
    AJ: { error: true, color: '#f00' }, // Invalid Line Item for an Order Mod
    AK: { error: false, color: '#000' }, // CIOrderMod request is waiting for manual review
    S1: { error: false, color: '#000' }, // Weather forecast is not favorable for collection over requested area of interest.
    S2: { error: true, color: '#f00' }, // Cannot schedule collection requirement due to competing collection requirements
    P1: { error: true, color: '#f00' }, // Failure in downlink stage within CDP Collection System
    P3: { error: false, color: '#000' }, // Collected Imaged cloud cover does not meet collection requirement
    P2: { error: true, color: '#f00' }, // Failure in backhaul or ingest stage within CDP Production System
    TE: { error: true, color: '#f00' }, // Technical Anomaly
    FF: { error: true, color: '#f00' }, // The Tasking Feasibility request does not meet business rules
    null: {},
};

/**
 * This represents a Sigma Order in its base form.
 */
export type SigmaOrder = {
    bucket: string;
    data_level: string;
    delivery_type: string;
} & Order;

/**
 * This represents a Sigma Order that has been enhanced
 * with additional metadata. Such as the full capture
 * information for the items inside.
 */
export type SigmaOrderDetails = {
    scenes?: OSKScene[];
    item_ids?: string[];
} & SigmaOrder;

/* Actions */
const RESET_ORDER_DATA = 'RESET_ORDER_DATA';
export function resetOrderData() {
    return {
        type: RESET_ORDER_DATA,
        payload: {},
    };
}

const COMPUTE_ORDER_READY_STATE = 'COMPUTE_ORDER_READY_STATE';
export function computeOrderReadyState() {
    return {
        type: COMPUTE_ORDER_READY_STATE,
        payload: {},
    };
}

const SET_LOADING_ORDERS = 'SET_LOADING_ORDERS';
export function setLoadingOrders(isLoading: boolean) {
    return {
        type: SET_LOADING_ORDERS,
        payload: {
            isLoading,
        },
    };
}

const SET_LOADING_SINGLE_ORDER = 'SET_LOADING_SINGLE_ORDER';
export function setLoadingSingleOrder(isLoading: boolean) {
    return {
        type: SET_LOADING_SINGLE_ORDER,
        payload: {
            isLoading,
        },
    };
}

const SET_ORDERS = 'SET_ORDERS';
export function setOrders(pageNumber: number, orders: SigmaOrder[]) {
    return {
        type: SET_ORDERS,
        payload: {
            pageNumber,
            orders,
        },
    };
}

const SET_ORDER_PAGE = 'SET_ORDER_PAGE';
export function setOrderPage(pageNumber: number) {
    return {
        type: SET_ORDER_PAGE,
        payload: {
            pageNumber,
        },
    };
}

const SET_ORDER_TOTALS = 'SET_ORDER_TOTALS';
export function setOrderTotals(total: number) {
    return {
        type: SET_ORDER_TOTALS,
        payload: {
            total,
        },
    };
}

const SET_ACTIVE_ORDER = 'SET_ACTIVE_ORDER';
export function setActiveOrder(order: SigmaOrderDetails) {
    return {
        type: SET_ACTIVE_ORDER,
        payload: {
            order,
        },
    };
}

const SET_ACTIVE_ORDER_ID = 'SET_ACTIVE_ORDER_ID';
export function setActiveOrderId(orderId: string) {
    return {
        type: SET_ACTIVE_ORDER_ID,
        payload: {
            orderId,
        },
    };
}

const SET_ORDER_NETWORK_ERROR = 'SET_ORDER_NETWORK_ERROR';
export function setOrderNetworkError(isError: boolean) {
    return {
        type: SET_ORDER_NETWORK_ERROR,
        payload: {
            isError,
        },
    };
}

const SET_ORDERS_USER_FILTER = 'SET_ORDERS_USER_FILTER';
export function setOrdersUserFilter(user?: number) {
    return {
        type: SET_ORDERS_USER_FILTER,
        payload: {
            user,
        },
    };
}

export function fetchOrderAsync(programId: number, orderId: string) {
    return (dispatch: AppDispatch) => {
        dispatch(setLoadingSingleOrder(true));
        dispatch(setOrderNetworkError(false));

        SigmaAPI.getOrder({
            id: orderId,
            program: programId,
        })
            .then(async (resp) => {
                // Fetch and populate the 'scenes' property for orders
                if (resp.data.inventoryId) {
                    // 'archive' orders
                    SigmaAPI.listCaptures({
                        orderId,
                        program: programId,
                        limit: 1000,
                    })
                        .then((captureResp) => {
                            const scenes = captureResp.data.results.map((capture) => SceneFromCapture(capture));
                            dispatch(
                                setActiveOrder({
                                    ...resp.data,
                                    scenes,
                                } as SigmaOrderDetails),
                            );
                        })
                        .catch((e) => {
                            console.error(e);
                            dispatch(setOrderNetworkError(true));
                        })
                        .finally(() => {
                            dispatch(setLoadingSingleOrder(false));
                            dispatch(computeOrderReadyState());
                        });
                } else if (resp.data.item_ids) {
                    // 'stac' orders
                    StacAPI.searchCatalog({ ids: resp.data.item_ids })
                        .then((response) => {
                            const scenes = (response.features as StacItem[]).map((feature) =>
                                SceneFromStacItem(feature),
                            );
                            dispatch(
                                setActiveOrder({
                                    ...resp.data,
                                    scenes,
                                } as SigmaOrderDetails),
                            );
                        })
                        .catch((e) => {
                            console.error(e);
                            dispatch(setOrderNetworkError(true));
                        })
                        .finally(() => {
                            dispatch(setLoadingSingleOrder(false));
                            dispatch(computeOrderReadyState());
                        });
                }
            })
            .catch(() => {
                dispatch(setLoadingSingleOrder(false));
                dispatch(computeOrderReadyState());
            });
    };
}

export function fetchOrdersAsync(programId: number, page: number) {
    return (dispatch: AppDispatch, getState: () => RootState) => {
        dispatch(setLoadingOrders(true));
        SigmaAPI.listOrders({
            program: programId,
            limit: SHORT_VISIBLE_PAGE_SIZE,
            offset: page * SHORT_VISIBLE_PAGE_SIZE,
            ordering: '-order_timestamp',
            requestedBy: getState().data.orders.ordersUserFilter,
        })
            .then((resp) => {
                if (resp.data.results && resp.data.count) {
                    dispatch(
                        setOrders(
                            page,
                            resp.data.results.map((data) => {
                                return {
                                    ...data,
                                } as SigmaOrderDetails;
                            }),
                        ),
                    );

                    dispatch(setOrderTotals(resp.data.count));
                } else {
                }
            })
            .finally(() => {
                dispatch(setLoadingOrders(false));
                dispatch(computeOrderReadyState());
            });
    };
}

/* Reducer */
type OrderStateType = {
    pageInitialized: boolean;
    loadingSingleOrder: boolean;
    loadingOrders: boolean;
    totalOrders: number;
    orderPage: number;
    ordersByPage: Record<number, SigmaOrder[]>;
    activeOrder?: SigmaOrderDetails;
    loadingStacOrders: boolean;
    activeOrderId?: string;
    isOrderNetworkError?: boolean;
    ordersUserFilter?: number;
};

const initialState: OrderStateType = {
    pageInitialized: false,
    loadingSingleOrder: false,
    orderPage: 0,
    ordersByPage: {},
    totalOrders: 0,
    loadingOrders: false,
    loadingStacOrders: false,
    activeOrder: undefined,
    activeOrderId: undefined,
    isOrderNetworkError: false,
    ordersUserFilter: undefined,
};

export default function reducer(state = initialState, action: any): OrderStateType {
    switch (action.type) {
        case RESET_ORDER_DATA: {
            return initialState;
        }

        case SET_ORDERS: {
            const { pageNumber, orders } = action.payload;
            return {
                ...state,
                ordersByPage: {
                    ...state.ordersByPage,
                    [pageNumber]: orders,
                },
            };
        }

        case SET_ORDER_PAGE: {
            const { pageNumber } = action.payload;
            return {
                ...state,
                orderPage: pageNumber,
            };
        }

        case SET_ORDER_TOTALS: {
            const { total } = action.payload;
            return {
                ...state,
                totalOrders: total,
            };
        }

        case SET_ACTIVE_ORDER_ID: {
            const { orderId } = action.payload;
            return {
                ...state,
                activeOrderId: orderId,
            };
        }

        case SET_ACTIVE_ORDER: {
            const { order } = action.payload;
            return {
                ...state,
                activeOrder: order,
            };
        }

        case SET_LOADING_ORDERS: {
            const { isLoading } = action.payload;
            return {
                ...state,
                loadingOrders: isLoading,
            };
        }

        case SET_LOADING_SINGLE_ORDER: {
            const { isLoading } = action.payload;
            return {
                ...state,
                loadingSingleOrder: isLoading,
            };
        }

        case COMPUTE_ORDER_READY_STATE: {
            return {
                ...state,
                pageInitialized: state.pageInitialized || (!state.loadingOrders && !state.loadingSingleOrder),
            };
        }

        case SET_ORDER_NETWORK_ERROR: {
            const { isError } = action.payload;
            return {
                ...state,
                isOrderNetworkError: isError,
            };
        }

        case SET_ORDERS_USER_FILTER: {
            const { user } = action.payload;

            return {
                ...state,
                ordersUserFilter: user,
            };
        }

        default:
            return { ...state };
    }
}

export const selectOrdersByPage = createSelector(
    [(state: RootState) => state.data.orders.ordersByPage, (state: RootState, pageNumber: number) => pageNumber],
    (orderPages, pageNumber): SigmaOrder[] => {
        return orderPages[pageNumber] ?? [];
    },
);

export const selectOrderDashboardReady = createSelector(
    [(state: RootState) => state.data.orders.pageInitialized],
    (pageInitialized) => {
        return pageInitialized;
    },
);

export const selectHasOrders = createSelector(
    [(state: RootState) => state.data.orders.ordersByPage],
    (orders: Record<any, any>): boolean => {
        return Object.values(orders).flatMap((x) => x).length > 0;
    },
);

export function selectOrderStatusCode(orderStatus?: OrderStatus | null): string {
    if (!orderStatus) return 'null';

    return orderStatus.reqLineStatusCode ?? 'null';
}

export function selectOrderStatus(orderStatus?: OrderStatus): string {
    if (!orderStatus) return 'unknown';

    if (['TE', 'AE'].includes(orderStatus.reqLineStatusCode ?? '')) {
        return 'order_failed';
    } else {
        return orderStatus.reqLineItemStatus;
    }
}

export function selectOrderFailed(order?: Order) {
    if (!order) return false;

    const status = order.orderStatus?.reqLineItemStatus;
    const statusCode = order.orderStatus?.reqLineStatusCode;

    // For mysterious reasons, this means the order is failed.
    if (['TE', 'AE'].includes(statusCode ?? '')) return true;
    // If there is no status, what does that even mean?
    if (!status) return false;
    // If the status is one of a known failure mode, return true
    return [
        OrderStatusReqLineItemStatusEnum.OrderCanceled,
        OrderStatusReqLineItemStatusEnum.OrderRejected,
        OrderStatusReqLineItemStatusEnum.OrderExpired,
        OrderStatusReqLineItemStatusEnum.ModRejected,
    ].includes(status);
}
